import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Button, useTheme } from '@mui/material'
import { toast } from 'react-toastify'

import { exportPracticeSessionsAuditReport, exportQuizzesAuditReport } from '../../../utils/auditing-export-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import { useUserStore } from '../../../store/user.store'
import MultiBarGraph from '../../common/MultiBarGraph'
import LineBarGraph from '../../common/LineBarGraph'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'
import LeaderboardDialog from '../../ai-mentor/LeaderboardDialog'


const facultyColumns = [
    { id: 1, field: "index", index: true, name: 'Sl. No.', width: "100px", align: "center", padding: "5px 20px" },
    { id: 2, field: "faculty_name", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=false`, renderCell: row => row?.name?.toLowerCase() || row?.empcode, cellStyles: { textTransform: "capitalize" }, name: "Faculty", width: "200px", padding: "5px 20px" },
    { id: 3, field: "created_by", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=false`, renderCell: row => row?.empcode, name: 'Empcode', padding: "5px 20px" },
    { id: 6, field: "institute", filterable: true, cellStyles: { textTransform: "capitalize" }, name: "Institute", width: "100px", padding: "5px 20px" },
    { id: 6, field: "department", filterable: true, renderCell: row => `${row.department?.toLowerCase()}`, cellStyles: { textTransform: "capitalize" }, name: "Department", width: "250px", padding: "5px 20px" },
    { id: 4, field: "count", dataType: "number", sortable: true, filterable: true, align: "center", style: { align: "center" }, name: "Practice Sessions", width: "180px", padding: "5px 20px" },
    { id: 4, field: "attempts", dataType: "number", sortable: true, filterable: true, align: "center", style: { align: "center" }, name: "Attempts", width: "200px", padding: "5px 20px" },
    { id: 4, field: "subjects", dataType: "number", sortable: true, renderCell: row => `${row.subjects?.length || 0}`, align: "center", style: { align: "center" }, name: "Subjects", padding: "5px 20px" },
]

const studentColumns = [
    { id: 1, field: "index", index: true, name: 'Sl. No.', width: "100px", align: "center", padding: "5px 20px" },
    { id: 2, field: "name", filterable: true, width: "150px", cellStyles: { textTransform: "capitalize" }, name: "Student name", padding: "5px 20px" },
    { id: 3, field: "id", filterable: true, name: 'AUID', padding: "5px 20px" },
    { id: 6, field: "institute", filterable: true, cellStyles: { textTransform: "capitalize" }, name: "Institute", width: "100px", padding: "5px 20px" },
    { id: 6, field: "branch", filterable: true, renderCell: row => ( !row.course_short_name && !row.course_branch_short_name ) ? "NA" : `${row.course_short_name || ""}${row.course_short_name ? `-${row.course_branch_short_name}` : row.course_branch_short_name || ""}`, cellStyles: { textTransform: "capitalize" }, name: "Branch", width: "200px", padding: "5px 20px" },
    { id: 4, field: "count", dataType: "number", sortable: true, filterable: true, align: "center", style: { align: "center" }, name: "Practices Made", width: "200px", padding: "5px 20px" },
]

const PracticeSessionsStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortByAttemptsCount: "0", sortByQuizzesCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const statsType = searchParams.get( 'stats' ) || "faculty"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const filters = searchParams.getAll( "filter" )
    const sorts = searchParams.getAll( "sort" )

    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [studentStats, setStudentStats] = useState( [] )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [leaderboardDialogOpen, setLeaderboardDialogOpen] = useState( false )

    const { layout, palette } = useTheme()
    const navigate = useNavigate()

    const UserStore = useUserStore()

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats = [], last12MonthsStats = {}, studentStats = [] } } } = await api.audit.getPracticeSessionsStats( { month, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setFacultyStats( facultyStats )
            setStudentStats( studentStats )
            if ( Object.keys( last12MonthsStats ).length > 0 )
                setLast12MonthsStats( last12MonthsStats )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the practice sessions audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute] )


    const getGraphData = useCallback( async () => {
        try {
            setLoadingGraphData( true )
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getPracticeSessionsGraph( { month, institute, department } )

            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the practice sessions graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment] )


    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )

    const monthWiseGraphData = useMemo( () => {
        const data = last12MonthsStats || {}
        if ( selectedInstitute === "all" ) {
            const formattedData = {}
            const institutes = Object.keys( data )
            for ( const institute of institutes ) {
                for ( const month in data[institute] ) {
                    if ( !Object.hasOwn( data[institute], month ) ) continue

                    const { practice_sessions = 0 } = data[institute][month] || {}
                    if ( !formattedData[month] )
                        formattedData[month] = { month, count: 0 }
                    formattedData[month].count += +practice_sessions

                }
            }
            return Object.values( formattedData )

        } else {
            const formattedData = {}
            for ( const month in data[selectedInstitute] ) {
                if ( !Object.hasOwn( data[selectedInstitute], month ) ) continue

                const { practice_sessions = 0 } = data[selectedInstitute][month] || {}
                if ( !formattedData[month] )
                    formattedData[month] = { month, count: 0 }
                formattedData[month].count += +practice_sessions

            }
            return Object.values( formattedData )
        }
    }, [last12MonthsStats, selectedInstitute] )

    const filtered = useMemo( () => {
        if ( selectedDepartment === "all" || !selectedDepartment )
            return facultyStats

        const departemtnId = +selectedDepartment?.split( '-' ).at( 0 )
        return facultyStats.filter( ps => +ps?.department_id === departemtnId )
    }, [selectedDepartment, facultyStats] )


    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* <Button onClick={getData}>refresh</Button> */}
            {leaderboardDialogOpen && <LeaderboardDialog open={leaderboardDialogOpen} setOpen={setLeaderboardDialogOpen} />}

            {/* Filters */}
            <AuditPageStatsFilterBar
                statsToggle
                showZeroResourcesSwitch={false}
                resource="practice sessions"
                listResourceBtnLabel="View Practice Sessions"
                title="Practice sessions statistics"
                customActions={[
                    {
                        label: "View Issues",
                        onClick: () => navigate( 'issues' ),
                        buttonVariant: "outlined",
                        buttonStartIcon: Icons.default.ReportOutlined,
                        visible: UserStore.getUser?.user_role?.toUpperCase() === "ADMIN"
                    },
                    {
                        label: "View Leaderboard",
                        onClick: () => setLeaderboardDialogOpen( true ),
                        buttonVariant: "outlined",
                        buttonStartIcon: Icons.default.AnalyticsIcon,
                        visible: ["ADMIN", "PRINCIPAL", "SUBADMIN"].includes( UserStore.getUser?.user_role?.toUpperCase() )
                    }
                ]}
            />

            {/* Table */}
            {!statsError && !loading && view === 'tabular' && statsType === 'faculty' && <CustomTable
                pageSize={50}
                columns={facultyColumns}
                data={filtered}
                tableStyles={{
                    minHeight: "350px",
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export Filtered",
                        visible: ( filters.length > 0 || sorts.length > 0 ),
                        startIcon: Icons.default.Publish,
                        onClick: ( filtered ) => exportPracticeSessionsAuditReport( filtered, studentStats, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-practice-sessions-audit-report` )
                    },
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: () => exportPracticeSessionsAuditReport( filtered, studentStats, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-practice-sessions-audit-report` )
                    }
                ]}
            />}

            {!statsError && !loading && view === 'tabular' && statsType === 'student' && <CustomTable
                pageSize={50}
                columns={studentColumns}
                data={studentStats}
                tableStyles={{
                    minHeight: "350px",
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export Filtered",
                        visible: ( filters.length > 0 || sorts.length > 0 ),
                        startIcon: Icons.default.Publish,
                        onClick: ( filtered ) => exportPracticeSessionsAuditReport( filtered, filtered, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-practice-sessions-audit-report` )
                    },
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: () => exportPracticeSessionsAuditReport( filtered, studentStats, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-practice-sessions-audit-report` )
                    }
                ]}
            />}

            {/* Retry */}
            {statsError && <Box display="flex" justifyContent="center">
                <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />
            </Box>}

            {/* Graph */}
            {
                !loading && !loadingGraphData && view !== 'tabular' &&
                <Box display="flex" color={palette.common.font} flexGrow={1} minHeight="400px">
                    {!graphError ? <Box flexGrow={1} display="flex" flexDirection={{ md: "row", xs: "column" }} gap="10px">
                        <LineBarGraph graphTitle='30 days usage' lineLegend='Practice session' barLegend='Attempts' showMenuBar={false} yAxisName="Practice sessions" y2AxisName='Attempts' xAxisName="Date" lineData={graphData} barData={graphData} barValuesKey='attempts' lineValuesKey='practice_sessions' dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} />
                        <MultiBarGraph xAxisLabel="Month" xAxisKey='month' seriesConfig={[{ key: 'count', label: 'Practice Sessions', color: '#f37509' }]} title="Last 12 months usage" data={monthWiseGraphData} />
                    </Box>
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }

            {/* Loading */}
            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner padding='0' waitingMessage="Loading practice sessions audit report please..." />}
        </Box >
    )
}

export default PracticeSessionsStats
